/**
 * <video-component> — video.js wrapper supporting html5 / youtube / vimeo,
 * lazy-load via <template> hydration, IntersectionObserver autoplay-in-view,
 * and integration with swiper slide-changed events.
 *
 * Ported from repeats project. Uses window.videojs (ES modules cannot see bare videojs global).
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/responsive
 *   - window.videojs (loaded dynamically via window.loadScript)
 *   - window.theme.videoScript (theme.liquid inline config)
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';

class VideoComponent extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.options = JSON.parse(
      this.element.getAttribute('data-section-settings')
    );
    this.videoType = this.options['videoType'] ?? this.options['video_type'] ?? null;
    this.isTextDisabled = this.element.getAttribute('data-text-disable');
    this.sectionId = this.element.getAttribute('data-section-id');
    this.trueObserver = false;
    this.delegateElement = new Delegate(this.element);
    this.videoPlayer = null;
    this.playOverlay = null;
    this.isInitialized = false;

    const isDesktop = !responsive.matchesBreakpoint('phone') || !responsive.matchesBreakpoint('pocket');
    const position = this.element.dataset.position;

    // Players hydrate lazily. Building every player up front costs ~100MB of media
    // memory on a media-heavy page, which is enough to get the tab killed on iOS.
    if (isDesktop) {
      this.setupScrollBasedLoading();
    } else {
      this.bindEvents();

      const slide = this.element.closest('.swiper-slide');
      const isActiveSlide =
        position == 0 ||
        slide?.classList.contains('swiper-slide-active') ||
        slide?.classList.contains('swiper-slide-duplicate-active');

      if (isActiveSlide) {
        this.checkLoaded();
      } else {
        // Not in view and not the active slide — wait until it is.
        this.setupScrollBasedLoading();
      }
    }
  }

  setupScrollBasedLoading() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isInitialized) {
          observer.unobserve(this.element);
          this.isInitialized = true;
          this.checkLoaded();
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '100px'
    });

    observer.observe(this.element);
  }

  bindEvents() {
    window.removeEventListener(
      'swiper:slide-changed',
      this.onSlideChangeListener
    );
    this.onSlideChangeListener = this.onSlideChange.bind(this);
    window.addEventListener('swiper:slide-changed', this.onSlideChangeListener);
  }

  #usesCustomControls() {
    return Boolean(
      this.element.querySelector('[data-video-controls], .section-video__controls--bar')
    );
  }

  /** Autoplay / no-controls videos must stay muted so sound never plays without user consent. */
  #effectiveMuted() {
    return !this.options.controls || Boolean(this.options.muted);
  }

  #enforceMutedPolicy() {
    if (!this.videoPlayer || this.options.controls) {
      return;
    }

    if (!this.videoPlayer.muted()) {
      this.videoPlayer.muted(true);
    }
  }

  #bindMutedPolicy() {
    if (this.options.controls || !this.videoPlayer) {
      return;
    }

    this.videoPlayer.on('volumechange', () => {
      this.#enforceMutedPolicy();
    });

    this.videoPlayer.on('play', () => {
      this.#enforceMutedPolicy();
    });

    this.#enforceMutedPolicy();
  }

  bindCustomControls() {
    this.stateElements = this.element.querySelectorAll("[data-action='toggle-state']");
    this.soundElements = this.element.querySelectorAll("[data-action='toggle-sound']");

    if (this.stateElements.length && this.soundElements.length && this.videoPlayer) {
      this.delegateElement.on('click', "[data-action='toggle-state']", (event) => {
        event.stopPropagation();
        this.videoPlayer.paused() ? this.videoPlayer.play() : this.videoPlayer.pause();
      });

      this.delegateElement.on('click', "[data-action='toggle-sound']", (event) => {
        event.stopPropagation();
        this.videoPlayer.muted() ? this.videoPlayer.muted(false) : this.videoPlayer.muted(true);
      });

      this.videoPlayer.on('play', () => {
        this.stateElements.forEach((el) => el.setAttribute('aria-label', 'playing'));
      });

      this.videoPlayer.on('pause', () => {
        this.stateElements.forEach((el) => el.setAttribute('aria-label', 'paused'));
      });

      const syncSoundLabel = () => {
        this.soundElements.forEach((el) =>
          el.setAttribute('aria-label', this.videoPlayer.muted() ? 'muted' : 'unmuted')
        );
      };

      this.videoPlayer.on('volumechange', syncSoundLabel);
      syncSoundLabel();
    }

    this.#bindProgressBar();
  }

  bindVideoAreaToggle() {
    if (!this.options['controls'] || !this.videoPlayer) {
      return;
    }

    this.togglePlayHandler = (event) => {
      if (event?.target?.closest?.('[data-video-controls]')) {
        return;
      }
      if (event?.target?.closest?.('.vjs-control-bar')) {
        return;
      }

      if (this.videoPlayer.paused()) {
        const playPromise = this.videoPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      } else {
        this.videoPlayer.pause();
      }
    };

    if (this.videoPlayer.techName_ === 'Html5') {
      this.videoPlayer.on('touchstart', this.togglePlayHandler);
    } else {
      this.videoPlayer.on('click', this.togglePlayHandler);
    }
  }

  #bindProgressBar() {
    const progressRoot = this.element.querySelector('[data-video-progress]');
    const track = this.element.querySelector('[data-video-progress-track]');
    const fill = this.element.querySelector('[data-video-progress-fill]');
    const thumb = this.element.querySelector('[data-video-progress-thumb]');

    if (!progressRoot || !track || !fill || !this.videoPlayer || progressRoot.dataset.progressBound === 'true') {
      return;
    }

    progressRoot.dataset.progressBound = 'true';

    this.updateProgressBar = () => {
      const duration = this.videoPlayer.duration();
      if (!duration || !Number.isFinite(duration)) {
        return;
      }

      const percent = (this.videoPlayer.currentTime() / duration) * 100;
      fill.style.width = `${percent}%`;
      if (thumb) {
        thumb.style.left = `${percent}%`;
      }
      progressRoot.setAttribute('aria-valuenow', String(Math.round(percent)));
    };

    this.videoPlayer.on('timeupdate', this.updateProgressBar);
    this.videoPlayer.on('loadedmetadata', this.updateProgressBar);
    this.videoPlayer.on('durationchange', this.updateProgressBar);

    this.seekFromClientX = (clientX) => {
      const duration = this.videoPlayer.duration();
      if (!duration || !Number.isFinite(duration)) {
        return;
      }

      const rect = track.getBoundingClientRect();
      if (!rect.width) {
        return;
      }

      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      this.videoPlayer.currentTime(ratio * duration);
      this.updateProgressBar();
    };

    this.onProgressClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.seekFromClientX(event.clientX);
    };

    this.onProgressKeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const duration = this.videoPlayer.duration();
      if (!duration || !Number.isFinite(duration)) {
        return;
      }

      const step = duration * 0.05;
      let nextTime = this.videoPlayer.currentTime();

      if (event.key === 'ArrowLeft') nextTime -= step;
      if (event.key === 'ArrowRight') nextTime += step;
      if (event.key === 'Home') nextTime = 0;
      if (event.key === 'End') nextTime = duration;

      this.videoPlayer.currentTime(Math.min(duration, Math.max(0, nextTime)));
      this.updateProgressBar();
    };

    progressRoot.addEventListener('click', this.onProgressClick);
    progressRoot.addEventListener('keydown', this.onProgressKeydown);

    this.isProgressDragging = false;

    this.onProgressPointerMove = (event) => {
      if (!this.isProgressDragging) {
        return;
      }
      this.seekFromClientX(event.clientX);
    };

    this.onProgressPointerUp = () => {
      this.isProgressDragging = false;
      window.removeEventListener('pointermove', this.onProgressPointerMove);
      window.removeEventListener('pointerup', this.onProgressPointerUp);
    };

    this.onProgressPointerDown = (event) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.isProgressDragging = true;
      this.seekFromClientX(event.clientX);
      window.addEventListener('pointermove', this.onProgressPointerMove);
      window.addEventListener('pointerup', this.onProgressPointerUp);
    };

    progressRoot.addEventListener('pointerdown', this.onProgressPointerDown);
  }

  syncPlayOverlay() {
    if (!this.playOverlay || !this.options.controls || !this.videoPlayer) return;

    const isPaused = this.videoPlayer.paused();
    this.playOverlay.hidden = !isPaused;
    this.playOverlay.setAttribute('aria-hidden', isPaused ? 'false' : 'true');
  }

  bindPlayOverlay() {
    if (!this.options.controls) return;

    this.playOverlay = this.element.querySelector('.video-component__play-overlay');
    if (!this.playOverlay) return;

    this.playOverlay.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.videoPlayer?.paused()) return;

      const playPromise = this.videoPlayer.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    });

    this.videoPlayer.on('play', () => this.syncPlayOverlay());
    this.videoPlayer.on('pause', () => this.syncPlayOverlay());
    this.videoPlayer.on('ended', () => this.syncPlayOverlay());

    this.syncPlayOverlay();
  }

  bindHoverEvents() {
    if (this.options['play_in_view_mob_hover_desk']) {
      this.onMouseEnterListener = this.onMouseEnter.bind(this);
      this.onMouseLeaveListener = this.onMouseLeave.bind(this);

      this.element.addEventListener('mouseenter', this.onMouseEnterListener);
      this.element.addEventListener('mouseleave', this.onMouseLeaveListener);
    }
  }

  onMouseEnter() {
    const isMobile = responsive.matchesBreakpoint('pocket');

    if (!isMobile && this.videoPlayer && this.options['play_in_view_mob_hover_desk'] && !this.options['play_in_view']) {
      this.#enforceMutedPolicy();
      const playPromise = this.videoPlayer.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }

  onMouseLeave() {
    const isMobile = responsive.matchesBreakpoint('pocket');

    if (!isMobile && this.videoPlayer && this.options['play_in_view_mob_hover_desk'] && !this.options['play_in_view']) {
      if (this.videoPlayer.el_.classList.contains('vjs-playing')) {
        this.videoPlayer.pause();
      }
    }
  }

  checkLoaded() {
    var _this = this;
    if (typeof window.videojs !== 'undefined') {
      this.videoPlayerInitialization();
      if (
        this.videoPlayer &&
        !this.options['play_in_view'] &&
        !this.options['play_in_view_mob_hover_desk']
      ) {
        this.videoPlayer.pause();
      }
    } else {
      window.loadScript?.(window.theme.videoScript);
      setTimeout(function () {
        _this.checkLoaded();
      }, 100);
    }
  }

  onSlideChange(event) {
    this.elementPosition = parseInt(this.element.getAttribute('data-position'));
    const isMobile = responsive.matchesBreakpoint('pocket');

    if (event.detail.currentIndex == this.elementPosition) {
      if (this.element.getAttribute('loaded')) {
        if (!isMobile && this.videoPlayer) {
          this.reinitializeVideoPlayer();
        } else if (!this.videoPlayer) {
          this.checkLoaded();
        }
      } else {
        var node = document.createElement('div');
        node.appendChild(
          this.element
            .querySelector('template')
            .content.firstElementChild.cloneNode(true)
        );
        this.element.appendChild(node.firstElementChild).focus();
        this.element.setAttribute('loaded', true);
        this.checkLoaded();
      }

      if ((this.options['play_in_view']) || (this.options['play_in_view_mob_hover_desk'] && isMobile)) {
        setTimeout(() => {
          if (this.videoPlayer) {
            this.#enforceMutedPolicy();
            const playPromise = this.videoPlayer.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
            this.createObserver();
          }
        }, 200);
      }
    } else if (
      this.videoPlayer &&
      this.videoPlayer.el_.classList.contains('vjs-playing')
    ) {
      this.videoPlayer.pause();
    }
  }

  videoPlayerInitialization() {
    if (!this.element.getAttribute('loaded')) {
      const node = document.createElement('div');
      node.appendChild(
        this.element
          .querySelector('template')
          .content.firstElementChild.cloneNode(true)
      );
      this.element.appendChild(node.firstElementChild).focus();
      this.element.setAttribute('loaded', true);
    }

    const isMobile = responsive.matchesBreakpoint('pocket');

    let aspect_ratio = '';
    let src = '';
    let host = 'html5';

    if (isMobile) {
      aspect_ratio = this.options['aspect_mob'] || '9:16';

      if (this.videoType == 'video_inside_media') {
        const sources = this.options['videod']['sources'];
        let topQuality = null;
        sources.forEach((obj) => {
          if (obj.format === 'mp4') {
            if (!topQuality) topQuality = obj;
            if (topQuality.height < obj.height) topQuality = obj;
          }
        });
        const aspectRatioHeight = Math.floor(
          100 / this.options['videod']['aspect_ratio']
        );
        if (!this.options['video_ratio']) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
        src = topQuality['url'] + '#t=0.001';
      } else if (this.videoType == 'video_inside_media_external') {
        const aspectRatioHeight = Math.floor(
          100 / this.options['videom']['aspect_ratio']
        );
        if (!this.options['video_ratio']) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
        host = this.options['videom']['host'];
        if (host == 'youtube') {
          src =
            'https://www.youtube.com/watch?v=' +
            this.options['videom']['external_id'];
        } else {
          src = this.options['videom']['external_id'];
        }
      } else {
        if (this.options['videom'].includes('youtube')) {
          host = 'youtube';
        } else if (this.options['videom'].includes('vimeo')) {
          host = 'vimeo';
        }
        src = this.options['videom'];
      }
    } else {
      aspect_ratio = this.options['aspect_desk'] || '16:9';

      if (this.videoType == 'video_inside_media') {
        const sources = this.options['videod']['sources'];
        let topQuality = null;
        sources.forEach((obj) => {
          if (obj.format === 'mp4') {
            if (!topQuality) topQuality = obj;
            if (topQuality.height < obj.height) topQuality = obj;
          }
        });
        src = topQuality['url'] + '#t=0.001';
        const aspectRatioHeight = Math.floor(
          100 / this.options['videod']['aspect_ratio']
        );

        if (!this.options['video_ratio']) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
      } else if (this.videoType == 'video_inside_media_external') {
        host = this.options['videod']['host'];
        const aspectRatioHeight = Math.floor(
          100 / this.options['videod']['aspect_ratio']
        );
        if (!this.options['video_ratio']) {
          aspect_ratio = `100:${aspectRatioHeight}`;
        }
        if (host == 'youtube') {
          src =
            'https://www.youtube.com/watch?v=' +
            this.options['videod']['external_id'];
        } else {
          src = this.options['videod']['external_id'];
        }
      } else {
        if (this.options['videod'].includes('youtube')) {
          host = 'youtube';
        } else if (this.options['videod'].includes('vimeo')) {
          host = 'vimeo';
        }
        src = this.options['videod'];
      }
    }
    if (!src) {
      src = 'https://www.youtube.com/watch?v=_9VUPq3SxOc';
      host = 'youtube';
    }
    const type =
      host === 'youtube'
        ? 'video/youtube'
        : host === 'vimeo'
          ? 'video/vimeo'
          : 'video/mp4';

    aspect_ratio = aspect_ratio.replace('/', ':');
    // video.js requires a strict "W:H" aspect ratio and throws on a bare number. The designed-ratio
    // setting can be a single value like "1" (valid for CSS aspect-ratio, but not video.js), so a
    // value without a colon is normalised to "N:1" (CSS treats a lone N as N/1).
    if (!aspect_ratio.includes(':')) {
      aspect_ratio = `${aspect_ratio}:1`;
    }

    const useCustomControls = this.options['controls'] && this.#usesCustomControls();

    const payload = {
      aspectRatio: aspect_ratio,
      autoplay: false,
      muted: this.#effectiveMuted(),
      loop: this.options['loop'],
      controls: false,
      poster: false,
      preload: 'metadata',
      playsinline: true,
      responsive: true,
      techOrder: [host],
      disablePictureInPicture: true,
      bigPlayButton: false,
      controlBar: false,
      sources: [
        {
          type: type,
          src: src,
        },
      ],
    };

    if (this.options['controls'] && !useCustomControls) {
      payload.controls = true;
      payload.bigPlayButton = true;
      payload.controlBar = {
        children: [
          'playToggle',
          'progressControl',
          'CustomControlSpacer',
          'MuteToggle',
          'fullscreenToggle',
        ],
      };
    }

    const videoplayerID = this.element.querySelector('video');
    this.videoPlayer = window.videojs(videoplayerID, payload);

    const _this = this;
    this.videoPlayer.on('ready', function () {
      const holder = _this.element.querySelector('.videoHolder');
      if (holder) {
        holder.style.display = 'block';
      }

      _this.bindHoverEvents();
      _this.#bindMutedPolicy();
      _this.element.classList.toggle(
        'video-component--fwm-carousel',
        _this.isInFullWidthMediaCarousel()
      );

      if (_this.trueObserver == false && (_this.options['play_in_view_mob_hover_desk'] || _this.options['play_in_view'])) {
        _this.createObserver();
      }

      if (_this.options.controls && _this.#usesCustomControls()) {
        _this.videoPlayer.controls(false);
        _this.bindCustomControls();
        _this.bindVideoSurfaceTapToggle();
        return;
      }

      const isMobile = responsive.matchesBreakpoint('pocket');
      if (!isMobile) {
        setTimeout(() => {
          _this.ensureDesktopControls();
        }, 100);
      }

      if (_this.options.controls) {
        _this.bindPlayOverlay();
        if (_this.isInFullWidthMediaCarousel()) {
          _this.bindVideoSurfaceTapToggle();
        }
      }
    });

    if (this.#effectiveMuted() && host == 'vimeo') {
      this.videoPlayer.on('play', function () {
        _this.#enforceMutedPolicy();
      });
    }

    if (this.options['controls'] && !this.#usesCustomControls()) {
      if (host == 'html5' && !this.isInFullWidthMediaCarousel()) {
        this.bindHtml5TouchToggle();
      }
      if (host == 'html5') return;
      this.videoPlayer.on('click', function (event) {
        if (event.target.closest('.vjs-control-bar')) {
          return;
        }

        _this.toggleVideoPlayPause();
      });
    } else if (!this.options['controls']) {
      this.videoPlayer.off('click');
      this.videoPlayer.off('touchstart');
      this.videoPlayer.controls(false);

      const videoEl = this.element.querySelector('.video-js');
      if (videoEl) {
        videoEl.style.cursor = 'default';

        if (!this.isInFullWidthMediaCarousel()) {
          // Keep touchstart passive so iOS Safari can begin a vertical page scroll.
          videoEl.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }, true);
        }
      }
    }
  }

  isInFullWidthMediaCarousel() {
    const fwm = this.element.closest('banner-hero');
    if (!fwm || fwm.classList.contains('full-width-media--single-slide')) return false;

    const carousel = fwm.querySelector(
      'swiper-init.full-width-media, .full-width-media_content'
    );
    if (!carousel) return false;

    const slides = carousel.querySelectorAll(
      '.swiper-slide:not(.swiper-slide-duplicate)'
    );
    return slides.length > 1;
  }

  toggleVideoPlayPause() {
    if (!this.videoPlayer) return;

    if (this.videoPlayer.paused()) {
      const playPromise = this.videoPlayer.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    } else {
      this.videoPlayer.pause();
    }
  }

  #shouldSkipVideoSurfaceTap(target) {
    return !!target?.closest?.(
      '.vjs-control-bar, [data-video-controls], .video-component__play-overlay, .vjs-big-play-button, button, a'
    );
  }

  bindVideoSurfaceTapToggle() {
    const surface = this.element.parentElement;
    if (!surface?.classList.contains('videoWrapper')) return;
    if (surface.dataset.videoSurfaceTapBound === 'true') return;

    surface.dataset.videoSurfaceTapBound = 'true';

    const TAP_THRESHOLD = 10;
    let startX = 0;
    let startY = 0;
    let isSwipeGesture = false;
    let suppressClick = false;

    const onClick = (event) => {
      if (!this.videoPlayer || suppressClick) return;
      if (this.#shouldSkipVideoSurfaceTap(event.target)) return;

      this.toggleVideoPlayPause();
    };

    const onTouchStart = (event) => {
      if (this.#shouldSkipVideoSurfaceTap(event.target)) return;

      const touch = event.touches[0];
      if (!touch) return;

      startX = touch.clientX;
      startY = touch.clientY;
      isSwipeGesture = false;
    };

    const onTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      if (Math.abs(touch.clientX - startX) > TAP_THRESHOLD) {
        isSwipeGesture = true;
      }
    };

    const onTouchEnd = (event) => {
      if (!this.videoPlayer || this.#shouldSkipVideoSurfaceTap(event.target) || isSwipeGesture) {
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) return;
      if (Math.abs(touch.clientX - startX) > TAP_THRESHOLD) return;

      this.toggleVideoPlayPause();
      suppressClick = true;
      window.setTimeout(() => {
        suppressClick = false;
      }, 400);
    };

    surface.addEventListener('click', onClick);
    surface.addEventListener('touchstart', onTouchStart, { passive: true });
    surface.addEventListener('touchmove', onTouchMove, { passive: true });
    surface.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  bindFwmCarouselTapToggle() {
    this.bindVideoSurfaceTapToggle();
  }

  bindHtml5TouchToggle() {
    const playerEl = this.videoPlayer?.el?.();
    if (!playerEl || playerEl.dataset.touchToggleBound === 'true') return;

    playerEl.dataset.touchToggleBound = 'true';

    const TAP_THRESHOLD = 10;
    const inCarousel = this.isInFullWidthMediaCarousel();
    let startX = 0;
    let startY = 0;
    let isSwipeGesture = false;

    const exceedsTapThreshold = (dx, dy) => {
      if (inCarousel) return dx > TAP_THRESHOLD;
      return dx > TAP_THRESHOLD || dy > TAP_THRESHOLD;
    };

    const onTouchStart = (event) => {
      if (event.target.closest('.vjs-control-bar')) return;

      const touch = event.touches[0];
      if (!touch) return;

      startX = touch.clientX;
      startY = touch.clientY;
      isSwipeGesture = false;
    };

    const onTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);

      if (exceedsTapThreshold(dx, dy)) {
        isSwipeGesture = true;
      }
    };

    const onTouchEnd = (event) => {
      if (event.target.closest('.vjs-control-bar')) return;
      if (isSwipeGesture) return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      const dx = Math.abs(touch.clientX - startX);
      const dy = Math.abs(touch.clientY - startY);

      if (exceedsTapThreshold(dx, dy)) return;

      this.toggleVideoPlayPause();
    };

    playerEl.addEventListener('touchstart', onTouchStart, { passive: true });
    playerEl.addEventListener('touchmove', onTouchMove, { passive: true });
    playerEl.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  createObserver() {
    const observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      threshold: 0.2,
      rootMargin: '200px 0px 200px 0px',
    });

    const element = this.element;
    observer.observe(element);
    this.trueObserver = true;
  }

  handleIntersect(entries) {
    const isMobile = responsive.matchesBreakpoint('pocket');

    entries.forEach((entry) => {
      if (entry.intersectionRatio === 1 || entry.isIntersecting == true) {
        if (!this.videoPlayer) {
          this.checkLoaded();
          return;
        }

        if (this.options['play_in_view'] && this.videoPlayer) {
          this.#enforceMutedPolicy();

          const checkIfPlaying = () => {
            if (!this.videoPlayer.el_.classList.contains('vjs-playing')) {
              const playPromise = this.videoPlayer.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
              setTimeout(checkIfPlaying, 100);
            }
          };

          checkIfPlaying();
        }
        else if (this.options['play_in_view_mob_hover_desk'] && this.videoPlayer && isMobile) {
          this.#enforceMutedPolicy();

          const checkIfPlaying = () => {
            if (!this.videoPlayer.el_.classList.contains('vjs-playing')) {
              const playPromise = this.videoPlayer.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
              setTimeout(checkIfPlaying, 100);
            }
          };

          checkIfPlaying();
        }
      } else if (
        entry.intersectionRatio === 0 ||
        entry.isIntersecting == false
      ) {
        if (this.options['play_in_view'] && this.videoPlayer &&
          this.videoPlayer.el_.classList.contains('vjs-playing')
        ) {
          this.videoPlayer.pause();
        }
        else if (this.options['play_in_view_mob_hover_desk'] && isMobile &&
          this.videoPlayer &&
          this.videoPlayer.el_.classList.contains('vjs-playing')
        ) {
          this.videoPlayer.pause();
        }
      }
    });
  }

  disposeVideoPlayer() {
    if (this.videoPlayer) {
      try {
        this.videoPlayer.dispose();
        this.videoPlayer = null;
      } catch (e) {
        this.videoPlayer = null;
      }
    }
  }

  ensureDesktopControls() {
    if (!this.videoPlayer) {
      return;
    }

    if (this.#usesCustomControls()) {
      this.videoPlayer.controls(false);
      return;
    }

    if (this.options['controls']) {
      this.videoPlayer.controls(true);

      const isMobile = responsive.matchesBreakpoint('pocket');
      if (!isMobile) {
        const textTrackDisplay = this.element.querySelector('.vjs-text-track-display');
        if (textTrackDisplay) {
          textTrackDisplay.style.pointerEvents = 'none';
        }
      }
    } else {
      this.videoPlayer.controls(false);

      const videoEl = this.element.querySelector('.video-js');
      if (videoEl) {
        videoEl.style.pointerEvents = 'none';
        videoEl.style.cursor = 'default';
      }
    }

  }

  reinitializeVideoPlayer() {
    this.disposeVideoPlayer();

    this.element.removeAttribute('loaded');
    this.element.removeAttribute('data-loaded');

    const existingHolder = this.element.querySelector('.videoHolder');
    if (existingHolder) {
      existingHolder.remove();
    }

    setTimeout(() => {
      this.checkLoaded();
    }, 50);
  }
}

if (!customElements.get('video-component')) {
  customElements.define('video-component', VideoComponent);
}
